import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { generateQuiz } from '../utils/quizGenerator';
import { QuizQuestion, QuizResult } from '../types';

const TOTAL_QUESTIONS = 20;

const QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get selected tenses from session storage
    const storedTenses = sessionStorage.getItem('selectedTenses');
    
    if (!storedTenses) {
      // If no tenses are selected, redirect to home page
      navigate('/');
      return;
    }
    
    const selectedTenses = JSON.parse(storedTenses);
    
    // Generate quiz questions
    const quizQuestions = generateQuiz(selectedTenses, TOTAL_QUESTIONS);
    setQuestions(quizQuestions);
    
    // Initialize results array
    const initialResults = quizQuestions.map(question => ({
      question,
      selectedAnswer: null,
      isCorrect: false,
    }));
    setResults(initialResults);
    
    setLoading(false);
  }, [navigate]);

  const handleAnswerSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAnswer(event.target.value);
  };

  const handleCheckAnswer = () => {
    if (!selectedAnswer) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    const isAnswerCorrect = selectedAnswer === currentQuestion.correctAnswer;
    
    // Update results
    const updatedResults = [...results];
    updatedResults[currentQuestionIndex] = {
      question: currentQuestion,
      selectedAnswer,
      isCorrect: isAnswerCorrect,
    };
    
    setResults(updatedResults);
    setIsAnswered(true);
    setIsCorrect(isAnswerCorrect);
    
    // Save progress to session storage
    sessionStorage.setItem('quizResults', JSON.stringify(updatedResults));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex === TOTAL_QUESTIONS - 1) {
      // If this is the last question, navigate to results page
      navigate('/results');
    } else {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">Loading quiz questions...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / TOTAL_QUESTIONS) * 100;

  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ textAlign: 'center', mb: 4 }}
      >
        Deux Baguettes
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid >
            <Typography variant="body1">
              Question {currentQuestionIndex + 1} of {TOTAL_QUESTIONS}
            </Typography>
          </Grid>
          <Grid >
            <Chip 
              label={`${Math.round(progress)}%`} 
              color="primary" 
              variant="outlined" 
            />
          </Grid>
        </Grid>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ mt: 1, height: 10, borderRadius: 5 }} 
        />
      </Box>
      
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Conjugate the verb "{currentQuestion.verb.infinitive}" ({currentQuestion.verb.english})
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Tense: {currentQuestion.tense.display}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="div" gutterBottom sx={{ fontWeight: 'bold' }}>
            {currentQuestion.pronoun} ...
          </Typography>
          
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <RadioGroup
                value={selectedAnswer || ''}
                onChange={handleAnswerSelect}
              >
                {currentQuestion.options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={<Radio />}
                    label={option}
                    disabled={isAnswered}
                    sx={{ 
                      mb: 1,
                      ...(isAnswered && option === currentQuestion.correctAnswer && {
                        color: 'success.main',
                        fontWeight: 'bold',
                      }),
                      ...(isAnswered && option === selectedAnswer && !isCorrect && {
                        color: 'error.main',
                      }),
                    }}
                  />
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        </Box>
        
        {isAnswered && (
          <Alert severity={isCorrect ? "success" : "error"} sx={{ mb: 3 }}>
            {isCorrect 
              ? "Correct! Well done!" 
              : `Incorrect. The correct answer is: ${currentQuestion.correctAnswer}`
            }
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {!isAnswered ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCheckAnswer}
              disabled={!selectedAnswer}
              sx={{ px: 4 }}
            >
              Check Answer
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNextQuestion}
              sx={{ px: 4 }}
            >
              {currentQuestionIndex === TOTAL_QUESTIONS - 1 ? 'See Results' : 'Next Question'}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default QuizPage;
